import {useCallback, useEffect, useMemo, useState} from 'react'

type LocalStorageValue = string | number | boolean | null

export function useLocalStorage<T extends LocalStorageValue>(key: string, initialValue: T): [T, (value: T) => void] {
  const getLocalStorage = useCallback((window: Window) => window.localStorage, [])
  return usePersistentStorage(key, initialValue, getLocalStorage)
}

interface Storage {
  getItem: (val: string) => string | null
  removeItem: (key: string) => void
  setItem: (key: string, value: string) => void
}

function usePersistentStorage<T extends LocalStorageValue>(
  key: string,
  defaultValue: T,
  getStorage: (window: Window) => Storage
): [T, (value: T) => void] {
  const storage = useMemo(() => (typeof window === 'undefined' ? undefined : getStorage(window)), [getStorage])

  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback(() => {
    // Prevent build error "window is undefined" but keep keep working
    if (typeof storage === 'undefined') {
      return defaultValue
    }

    try {
      const item = storage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      console.warn(`Error reading storage key “${key}”:`, error)
      return defaultValue
    }
  }, [defaultValue, key, storage])

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue)

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T) => {
      // Prevent build error "window is undefined" but keeps working
      if (typeof storage === 'undefined') {
        console.warn(`Tried setting storage key “${key}” even though environment is not a client`)
      }

      try {
        const newValue = value

        if (newValue === null) {
          storage?.removeItem(key)
        } else {
          // Save to local storage
          storage?.setItem(key, JSON.stringify(newValue))
        }

        // Save state
        setStoredValue(newValue)

        // We dispatch a custom event so every use<Local/Session>Storage hook is notified
        window.dispatchEvent(new Event('storage-changed'))
      } catch (error) {
        console.warn(`Error setting localStorage key “${key}”:`, error)
      }
    },
    [key, storage]
  )

  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue(readValue())
    }

    // this only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange)

    // this is a custom event, triggered in writeValueToLocalStorage
    window.addEventListener('storage-changed', handleStorageChange)

    // Clean up after unmounting
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('storage-changed', handleStorageChange)
    }
  }, [readValue])

  return useMemo(() => [storedValue, setValue], [setValue, storedValue])
}

export function useSessionStorage<T extends LocalStorageValue>(key: string, defaultValue: T): [T, (value: T) => void] {
  const getSessionStorage = useCallback((window: Window) => window.sessionStorage, [])
  return usePersistentStorage(key, defaultValue, getSessionStorage)
}
